import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { humanizeSupabaseError } from "@/lib/supabase-errors";
import type { WorkspaceTask } from "@/hooks/useWorkspaceTasks";
import type { WorkspaceProjectColumn } from "@/hooks/useWorkspaceProjects";

const KANBAN_SELECT = `id, workspace_id, title, description, due_date, due_at, status, outcome,
  completed_at, assigned_member_id, created_by_member_id, updated_at, priority, lead_id, tags,
  project_id, project_column_id, board_order_index,
  assignee:assigned_member_id(display_name),
  creator:created_by_member_id(display_name),
  lead:lead_id(id, name),
  subtasks:workspace_tasks!parent_task_id(id, status, title, assigned_member_id, due_date)`;

export const UNCATEGORIZED_COLUMN_ID = "__uncategorized__";

export type KanbanReorderUpdate = {
  taskId: string;
  columnId: string;
  orderIndex: number;
};

interface UseProjectKanbanTasksArgs {
  workspaceId: string | null;
  projectId: string | null;
  columns: WorkspaceProjectColumn[];
  memberId?: string | null;
  isAdmin?: boolean;
  onlyMine?: boolean;
  searchQuery?: string;
  enabled?: boolean;
}

export function useProjectKanbanTasks({
  workspaceId,
  projectId,
  columns,
  memberId,
  isAdmin = false,
  onlyMine = false,
  searchQuery = "",
  enabled = true,
}: UseProjectKanbanTasksArgs) {
  const [tasks, setTasks] = useState<WorkspaceTask[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchTasks = useCallback(async () => {
    if (!workspaceId || !projectId) {
      setTasks([]);
      return;
    }
    if (!enabled) return;

    setLoading(true);
    try {
      let query = supabase
        .from("workspace_tasks")
        .select(KANBAN_SELECT)
        .eq("workspace_id", workspaceId)
        .eq("project_id", projectId)
        .is("parent_task_id", null)
        .neq("status", "cancelled")
        .order("board_order_index", { ascending: true })
        .order("updated_at", { ascending: false });

      if ((!isAdmin || onlyMine) && memberId) {
        query = query.eq("assigned_member_id", memberId);
      }

      const trimmedSearch = searchQuery.trim();
      if (trimmedSearch) {
        query = query.ilike("title", `%${trimmedSearch.replace(/[%_\\]/g, "\\$&")}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      setTasks((data ?? []) as unknown as WorkspaceTask[]);
    } catch (err: unknown) {
      console.error("useProjectKanbanTasks.fetchTasks:", err);
      toast({
        title: "Erro ao carregar kanban",
        description: humanizeSupabaseError(err, {
          fallback: "Não foi possível carregar o kanban. Tente novamente.",
        }),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [workspaceId, projectId, memberId, isAdmin, onlyMine, searchQuery, enabled, toast]);

  useEffect(() => {
    void fetchTasks();
  }, [fetchTasks]);

  const tasksByColumn = useMemo(() => {
    const map = new Map<string, WorkspaceTask[]>();
    const columnIds = new Set(columns.map((c) => c.id));

    for (const col of columns) {
      map.set(col.id, []);
    }
    map.set(UNCATEGORIZED_COLUMN_ID, []);

    for (const task of tasks) {
      const rawKey = task.project_column_id ?? UNCATEGORIZED_COLUMN_ID;
      const key =
        rawKey === UNCATEGORIZED_COLUMN_ID || columnIds.has(rawKey)
          ? rawKey
          : UNCATEGORIZED_COLUMN_ID;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(task);
    }

    for (const [, list] of map) {
      list.sort((a, b) => (a.board_order_index ?? 0) - (b.board_order_index ?? 0));
    }

    return map;
  }, [tasks, columns]);

  const moveTaskToColumn = useCallback(
    async (taskId: string, columnId: string, orderIndex?: number) => {
      const { error } = await supabase.rpc("move_task_to_column", {
        p_task_id: taskId,
        p_column_id: columnId,
        p_order_index: orderIndex ?? null,
      });
      if (error) throw error;
      await fetchTasks();
    },
    [fetchTasks],
  );

  const optimisticBatchReorder = useCallback((updates: KanbanReorderUpdate[]) => {
    if (updates.length === 0) return;
    const byId = new Map(updates.map((u) => [u.taskId, u]));
    setTasks((prev) =>
      prev.map((t) => {
        const patch = byId.get(t.id);
        if (!patch) return t;
        return {
          ...t,
          project_column_id:
            patch.columnId === UNCATEGORIZED_COLUMN_ID ? null : patch.columnId,
          board_order_index: patch.orderIndex,
        };
      }),
    );
  }, []);

  const persistKanbanReorder = useCallback(
    async (updates: KanbanReorderUpdate[]) => {
      for (const { taskId, columnId, orderIndex } of updates) {
        if (columnId === UNCATEGORIZED_COLUMN_ID) continue;
        const { error } = await supabase.rpc("move_task_to_column", {
          p_task_id: taskId,
          p_column_id: columnId,
          p_order_index: orderIndex,
        });
        if (error) throw error;
      }
      await fetchTasks();
    },
    [fetchTasks],
  );

  return {
    tasks,
    tasksByColumn,
    loading,
    refetch: fetchTasks,
    moveTaskToColumn,
    optimisticBatchReorder,
    persistKanbanReorder,
    setTasks,
  };
}
